import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FontSizes, Heading } from '@hexure/ui';

const Container = styled.div`
  display: flex;
  max-height: 545px;
  overflow-x: auto;
  overflow-y: auto;
  flex-direction: column;
  padding: 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e5e5e5;
  margin-top: 20px;
  table-layout: fixed;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  border: 1px solid #ddd;
  font-size: ${FontSizes.DEFAULT};
  max-width: 50%;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const BandingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const BandingHeader = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #ccc;
  font-size: ${FontSizes.SMALL};
`;

const BandingCell = styled.td`
  padding: 8px;
  font-size: ${FontSizes.SMALL};
  border-bottom: 1px solid #eee;
`;

const ProductDetail = ({ carrier, data, productType, isUniversalQuoter = false }) => {
  const getMessage = (min, max, type) => {
    let message = 'N/A';

    const formatAmount = amount => {
      if (amount === null || isNaN(amount) || typeof amount === 'boolean' || amount === '') {
        return '';
      }
      return Number(amount).toLocaleString();
    };

    if (min === null && max === null) {
      return message;
    }

    if (type === 'premiums') {
      if (min !== undefined && max !== undefined) {
        message = `Min: $${formatAmount(min)} - Max: $${formatAmount(max)}`;
      } else if (min !== undefined) {
        message = `Min: $${formatAmount(min)}`;
      } else if (max !== undefined) {
        message = `Max: $${formatAmount(max)}`;
      }
    } else if (type === 'age') {
      if (min !== undefined && max !== undefined) {
        message = `Min: ${min} - Max: ${max}`;
      } else if (min !== undefined) {
        message = `Min: ${min}`;
      } else if (max !== undefined) {
        message = `Max: ${max}`;
      }
    }

    return message;
  };
  const isValidBonusValue = bonus => {
    if (!bonus) return false;
    const invalidValues = ['0', 'N/A', '–', '', null, undefined];
    return !invalidValues.includes(bonus);
  };

  const hasValidBonusBreakpoints = Array.isArray(data?.bonus_break_point) && data?.bonus_break_point.length > 0;
  const hasBonus = hasValidBonusBreakpoints && isValidBonusValue(data?.bonus);

  const renderBandingRows = () => {
    const breakpoints = Array.isArray(data?.bonus_break_point) ? data.bonus_break_point : [];

    if (breakpoints.length === 0) return null;

    const rawBonus = String(data?.bonus ?? '').replace('%', '');
    const bonusNumber = parseFloat(rawBonus);
    const displayBonus = !isNaN(bonusNumber) ? `+${bonusNumber.toFixed(2)}%` : (data?.bonus ?? '–');

    const termNumber = Number(data?.bonus_term) || 0;
    const durationText = data?.bonus_type === 'Interest' ? `${termNumber} Year${termNumber === 1 ? '' : 's'}` : '–';

    return breakpoints.map((bp, idx) => {
      const bpNumber = Number(bp) || 0;
      const formattedBP = `${bpNumber.toLocaleString()}+`;

      return (
        <tr key={idx}>
          <BandingCell>{`$${formattedBP}`}</BandingCell>
          <BandingCell>{displayBonus}</BandingCell>
          {data?.bonus_type === 'Interest' && <BandingCell>{durationText}</BandingCell>}
        </tr>
      );
    });
  };

  // Render simplified view for Universal Quoter
  if (isUniversalQuoter) {
    return (
      <Container>
        <Heading type='tertiary'>Product Details</Heading>
        <Table>
          <tbody>
            <TableRow>
              <TableCell>State Approved</TableCell>
              <TableCell>{data?.states_approved?.length > 0 ? data?.states_approved?.join(', ') : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Issue Ages</TableCell>
              <TableCell>{getMessage(data?.issue_min_age, data?.issue_max_age, 'age')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Premium Min</TableCell>
              <TableCell>{data?.premium_min ? `$${Number(data.premium_min).toLocaleString()}` : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Surrender Charge Period</TableCell>
              <TableCell>{data?.surrender_charge ? `${data?.surrender_charge} Year${data?.surrender_charge !== '1' ? 's' : ''}` : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Surrender Charge Schedule</TableCell>
              <TableCell>{data?.surrender_charge_schedule ? `${data?.surrender_charge_schedule}` : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Eapp Available</TableCell>
              <TableCell>{data?.fl_eapp ? data?.fl_eapp : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Free Withdrawals</TableCell>
              <TableCell>{data?.fee_withdrawals ? `${data?.fee_withdrawals}%` : 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Waivers</TableCell>
              <TableCell>{data?.waivers ? data?.waivers : 'N/A'}</TableCell>
            </TableRow>
          </tbody>
        </Table>
      </Container>
    );
  }

  return (
    <Container>
      <Heading type='tertiary'>Product Details</Heading>
      <Table>
        <tbody>
          <TableRow>
            <TableCell>Carrier Name</TableCell>
            <TableCell>{carrier?.name}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Product Name</TableCell>
            <TableCell>{data?.product_name ? data?.product_name : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Carrier's Rating</TableCell>
            <TableCell>{data?.carrier_rating ? data?.carrier_rating : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>State Approved</TableCell>
            <TableCell>{data?.states_approved?.length > 0 ? data?.states_approved?.join(', ') : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Issue Ages</TableCell>
            <TableCell>{getMessage(data?.issue_min_age, data?.issue_max_age, 'age')}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Premium Min/Max</TableCell>
            <TableCell>{getMessage(data?.premium_min ?? 'Not Available', data?.premium_max ?? 'Not Available', 'premiums')}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Surrender Charge Period</TableCell>
            <TableCell>{data?.surrender_charge ? `${data?.surrender_charge} Year` : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Surrender Charge Schedule</TableCell>
            <TableCell>{data?.surrender_charge_schedule ? `${data?.surrender_charge_schedule}` : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>FL Eapp</TableCell>
            <TableCell>{data?.fl_eapp ? data?.fl_eapp : 'N/A'}</TableCell>
          </TableRow>
          {productType !== 'Fixed Annuity' ? (
            <>
              <TableRow>
                <TableCell>Riders</TableCell>
                <TableCell>{data?.riders ? data?.riders : 'N/A'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>MVA</TableCell>
                <TableCell>{data?.mva ? data?.mva : 'N/A'}</TableCell>
              </TableRow>
            </>
          ) : null}
          {hasBonus && (
            <>
              <TableRow>
                <TableCell>Bonus</TableCell>
                <TableCell>{data?.bonus && data?.bonus !== '0' ? data?.bonus : 'N/A'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Premium Banding</TableCell>
                <TableCell>
                  <BandingTable>
                    <thead>
                      <tr>
                        <BandingHeader>Premium</BandingHeader>
                        <BandingHeader>Bonus</BandingHeader>
                        {data?.bonus_type === 'Interest' ? <BandingHeader>Duration</BandingHeader> : null}
                      </tr>
                    </thead>
                    <tbody>{renderBandingRows()}</tbody>
                  </BandingTable>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Bonus Rate Type</TableCell>
                <TableCell>{data.bonus_type || 'N/A'}</TableCell>
              </TableRow>
            </>
          )}
          <TableRow>
            <TableCell>Free Withdrawals</TableCell>
            <TableCell>{data?.fee_withdrawals ? `${data?.fee_withdrawals}%` : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Waivers</TableCell>
            <TableCell>{data?.waivers ? data?.waivers : 'N/A'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{productType !== 'Fixed Annuity' ? 'Minimum Guarantee' : 'Guaranteed Minimum Interest Rate'}</TableCell>
            <TableCell>
              {productType === 'Fixed Annuity'
                ? data?.minimum_guaranteed_interest_rate
                  ? `${data?.minimum_guaranteed_interest_rate}%`
                  : 'N/A'
                : data?.minimum_guaranteed_years
                  ? `${data?.minimum_guaranteed_years} years`
                  : 'N/A'}
            </TableCell>
          </TableRow>
        </tbody>
      </Table>
    </Container>
  );
};

ProductDetail.propTypes = {
  carrier: PropTypes.object,
  data: PropTypes.object,
  productType: PropTypes.string,
  isUniversalQuoter: PropTypes.bool
};

export default ProductDetail;
